import React, { createContext, useContext, useState, useEffect } from "react";
import apiService from "../api/apiService";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Get website token on mount
  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      // Check if we already have a valid token
      const existingToken = localStorage.getItem("family_tree_website_token");

      if (existingToken) {
        setToken(existingToken);
        setIsAuthenticated(true);
      } else {
        // Get new website token
        await getWebsiteToken();
      }
    } catch (error) {
      console.error("Auth initialization error:", error);
    } finally {
      setLoading(false);
    }
  };

  const getWebsiteToken = async () => {
    try {
      setLoading(true);
      const response = await apiService.getWebsiteToken();

      if (response.data?.data?.token) {
        const websiteToken = response.data.data.token;
        localStorage.setItem("family_tree_website_token", websiteToken);
        setToken(websiteToken);
        setIsAuthenticated(true);
        return { success: true, token: websiteToken };
      }

      return { success: false, error: "Failed to get website token" };
    } catch (error) {
      console.error("Get website token error:", error);
      return {
        success: false,
        error: error.message || "Failed to authenticate",
      };
    } finally {
      setLoading(false);
    }
  };

  const clearAuth = () => {
    setToken(null);
    setIsAuthenticated(false);
    localStorage.removeItem("family_tree_website_token");
  };

  const refreshWebsiteToken = async () => {
    try {
      await getWebsiteToken();
      return { success: true };
    } catch (error) {
      console.error("Token refresh error:", error);
      clearAuth();
      return { success: false, error: error.message };
    }
  };

  const value = {
    token,
    loading,
    isAuthenticated,
    getWebsiteToken,
    refreshWebsiteToken,
    clearAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
